/* ---------------------------------------------------------------------------
   What can be edited from the detail screen's edit dialog, and how.

   One config, two consumers: src/screens/editDialog.ts renders a labelled input
   per field, grouped into the same sections the detail screen itself uses, and
   src/core/store.ts's edit actions read it to seed a draft from a bridge and to
   turn the draft back into a PATCH body. Keeping the list in one place means a
   field can't be editable in the form but silently dropped on save, or vice
   versa.

   Deliberately excluded: `id` (the record's identity, not a fact about it) and
   `inspections` (a dated history log, not a field with one current
   value — editing that is a rows-of-its-own feature, not a form field).
   --------------------------------------------------------------------------- */

import type { Bridge } from './types.ts';

/**
 * How a field is typed into the form and parsed back out.
 *
 * `bool` is a three-state select rather than a checkbox, and that is the whole
 * reason it is its own kind: a checkbox has two states and the register needs
 * three. "Not recorded" has to be distinguishable from "recorded as no", or
 * every structure nobody has looked at asserts that somebody looked.
 */
export type FieldKind = 'text' | 'int' | 'float' | 'bool';

export interface EditField {
  key: keyof Bridge;
  label: string;
  kind: FieldKind;
  required?: boolean;
  /** Read from the bridge model when one is imported, so not asked for on New bridge. */
  modelled?: boolean;
  /** What the field is for, as the form's tooltip says it. */
  hint: string;
  /**
   * The usual values, offered as a dropdown. A value outside the list is kept
   * and still selectable — the register holds descriptive entries like "Masonry
   * gravity abutment on rock", and a list that could not show them would blank
   * them on the next save — and "Other…" types a new one.
   */
  options?: readonly string[];
  /** Or: the dropdown offers the values the register already holds for this field. */
  optionsFrom?: keyof Bridge;
}

export interface EditSection {
  title: string;
  fields: EditField[];
}

const SUPERSTRUCTURES = [
  'Reinforced concrete slab',
  'Reinforced concrete T-beam',
  'Reinforced concrete box girder',
  'Prestressed concrete I-girder',
  'Prestressed concrete box girder',
  'Steel plate girder',
  'Steel-concrete composite girder',
  'Steel truss',
  'Bailey (modular steel panel)',
  'Masonry arch',
  'Reinforced concrete arch',
  'Suspension',
  'Cable-stayed',
  'Box culvert',
] as const;

const FOUNDATIONS = [
  'Spread footing',
  'Pile foundation',
  'Well foundation',
  'Caisson',
  'Raft',
  'Founded on rock',
] as const;

export const EDIT_SECTIONS: EditSection[] = [
  {
    title: 'Identity',
    fields: [
      { key: 'name', label: 'Name', kind: 'text', required: true,
        hint: 'The structure’s name as the register and every report print it — usually the river or place it crosses.' },
      { key: 'inventoryCode', label: 'Inventory code', kind: 'text',
        hint: 'The department’s official inventory asset code (e.g. 38-F036-015), where one has been issued. Separate from the asset id.' },
      { key: 'route', label: 'Route code', kind: 'text', required: true,
        hint: 'The code of the road carried (e.g. N-35, H05, F045). Its prefix sets the road classification.' },
      { key: 'roadName', label: 'Road name', kind: 'text', required: true,
        hint: 'The name of the road carried, e.g. Karakoram Highway.' },
      { key: 'km', label: 'Chainage (km)', kind: 'float', required: true,
        hint: 'Distance along the road from its start to the structure, in kilometres. Orders structures along the road.' },
      { key: 'district', label: 'District', kind: 'text', required: true, optionsFrom: 'district',
        hint: 'The administrative district the structure is in. Used for grouping, filters and district rate tables.' },
      { key: 'province', label: 'Province', kind: 'text', required: true, optionsFrom: 'province',
        hint: 'The province the structure is in.' },
      { key: 'river', label: 'Crosses', kind: 'text', required: true,
        hint: 'What the structure spans: the river, stream, road or railway underneath.' },
      { key: 'type', label: 'Superstructure', kind: 'text', modelled: true, options: SUPERSTRUCTURES,
        hint: 'The deck’s structural form and material, e.g. reinforced concrete T-beam or steel truss.' },
      { key: 'spans', label: 'Spans', kind: 'int', modelled: true,
        hint: 'Number of spans between supports.' },
      { key: 'length', label: 'Total length (m)', kind: 'float', modelled: true,
        hint: 'Overall length of the structure between abutment faces, in metres.' },
      { key: 'deckWidth', label: 'Deck width (m)', kind: 'float', modelled: true,
        hint: 'Full width of the deck, kerbs and footpaths included, in metres.' },
      { key: 'carriageway', label: 'Carriageway (m)', kind: 'float', required: true,
        hint: 'Width available to traffic between kerbs, in metres.' },
      { key: 'year', label: 'Year built', kind: 'int', required: true,
        hint: 'Year the structure was completed or opened to traffic.' },
      { key: 'deckRL', label: 'Deck level (m)', kind: 'float',
        hint: 'Reduced level of the deck surface, in metres above datum. Compared with the HFL for freeboard.' },
      { key: 'hfl', label: 'HFL (m)', kind: 'float',
        hint: 'Highest flood level recorded at the site, in metres above the same datum as the deck level.' },
    ],
  },
  {
    title: 'General',
    fields: [
      { key: 'roadLength', label: 'Road length (km)', kind: 'float',
        hint: 'Total length of the road the structure carries, in kilometres.' },
      { key: 'divisionOffice', label: 'Division road office', kind: 'text', optionsFrom: 'divisionOffice',
        hint: 'The division office responsible for maintaining the structure.' },
      { key: 'lat', label: 'Latitude', kind: 'float',
        hint: 'Position in decimal degrees (WGS 84), north positive. Places the structure on the map.' },
      { key: 'lng', label: 'Longitude', kind: 'float',
        hint: 'Position in decimal degrees (WGS 84), east positive. Places the structure on the map.' },
      { key: 'deckBearing', label: 'Deck bearing (°)', kind: 'float',
        hint: 'Direction the deck runs, in degrees clockwise from north (0–180). Draws the structure’s footprint on the map.' },
      { key: 'overUnder', label: 'Over / under', kind: 'text', options: ['Over', 'Under'],
        hint: 'Whether the road passes over the obstacle (a bridge or overpass) or under it (an underpass).' },
      { key: 'loadRestriction', label: 'Load restriction (T)', kind: 'float',
        hint: 'Posted gross vehicle weight limit, in tonnes. Leave blank where none is posted.' },
      { key: 'heightRestriction', label: 'Height restriction (m)', kind: 'float',
        hint: 'Posted headroom limit, in metres — for through trusses and portals. Leave blank where none is posted.' },
      { key: 'designLoad', label: 'Design load', kind: 'text',
        options: ['IRC Class A', 'IRC Class AA', 'IRC Class 70R', 'IRC Class A + 70R', 'AASHTO HS20-44', 'AASHTO HS15-44', 'AASHTO HL-93', 'BS 5400 HA', 'BS 5400 HA + HB'],
        hint: 'The live-load standard the structure was designed to carry.' },
      { key: 'leftBankDistrict', label: 'Left bank district', kind: 'text', optionsFrom: 'district',
        hint: 'District on the left bank, looking downstream — where the river is a district boundary.' },
      { key: 'leftBankVdc', label: 'Left bank VDC', kind: 'text',
        hint: 'Village development committee or municipality on the left bank, looking downstream.' },
      { key: 'rightBankDistrict', label: 'Right bank district', kind: 'text', optionsFrom: 'district',
        hint: 'District on the right bank, looking downstream.' },
      { key: 'rightBankVdc', label: 'Right bank VDC', kind: 'text',
        hint: 'Village development committee or municipality on the right bank, looking downstream.' },
    ],
  },
  {
    title: 'Construction details',
    fields: [
      { key: 'spanLength', label: 'Span length (m)', kind: 'float',
        hint: 'Length of one span — the longest, where spans differ — in metres.' },
      { key: 'abutmentType', label: 'Abutment type', kind: 'text',
        options: ['Masonry gravity', 'Mass concrete gravity', 'RC cantilever', 'RC counterfort', 'Spill-through (open)', 'Integral', 'Box (buried)'],
        hint: 'Form of the end supports that carry the deck and retain the approach fill.' },
      { key: 'abutmentFoundation', label: 'Abutment foundation', kind: 'text', options: FOUNDATIONS,
        hint: 'How the abutments are founded.' },
      { key: 'pierType', label: 'Pier type', kind: 'text',
        options: ['Solid wall', 'Single column', 'Multi-column bent', 'Hammerhead', 'Masonry pier', 'Pile trestle', 'None (single span)'],
        hint: 'Form of the intermediate supports between spans.' },
      { key: 'pierFoundation', label: 'Pier foundation', kind: 'text', options: FOUNDATIONS,
        hint: 'How the piers are founded.' },
      { key: 'bearings', label: 'Bearings', kind: 'text',
        options: ['Elastomeric', 'Pot', 'Spherical', 'Sliding (PTFE)', 'Pin and roller', 'Rocker and roller', 'Steel plate', 'None (monolithic)'],
        hint: 'Type of bearing between the deck and its supports.' },
      { key: 'railings', label: 'Railings', kind: 'text',
        options: ['RC parapet', 'RC post and rail', 'Steel post and rail', 'Steel crash barrier', 'Masonry parapet', 'None'],
        hint: 'Type of parapet or railing along the deck edges.' },
      { key: 'movement', label: 'Movement', kind: 'text',
        options: ['Strip seal', 'Compression seal', 'Finger joint', 'Modular joint', 'Asphaltic plug', 'Buried joint', 'Open gap', 'None'],
        hint: 'Expansion joints that let the deck move with temperature.' },
      { key: 'riverTraining', label: 'River training', kind: 'text',
        options: ['Guide bunds', 'Spurs / groynes', 'Gabion revetment', 'Stone pitching', 'Launching apron', 'None'],
        hint: 'Works that guide the river through the structure and protect its banks and foundations.' },
      { key: 'runningSurface', label: 'Running surface', kind: 'text',
        options: ['Asphalt wearing course', 'Concrete wearing course', 'Bare concrete deck', 'Timber deck', 'Steel grating', 'Gravel'],
        hint: 'The wearing surface traffic runs on.' },
      { key: 'roadSigns', label: 'Road signs', kind: 'text',
        hint: 'Signs at the approaches — name board, load or height limit, speed, warnings — and their state.' },
      { key: 'services', label: 'Services', kind: 'text',
        hint: 'Utilities carried on or attached to the structure: water, power, telecom, gas.' },
    ],
  },
  {
    title: 'History',
    fields: [
      { key: 'designedBy', label: 'Designed by', kind: 'text',
        hint: 'The consultant or office that designed the structure.' },
      { key: 'constructedBy', label: 'Constructed by', kind: 'text',
        hint: 'The contractor that built the structure.' },
    ],
  },
  {
    /* What a closure costs, as against what might cause one. Road
       classification is absent because the route code above already carries it
       — see src/data/consequence.ts. */
    title: 'Consequence of closure',
    fields: [
      { key: 'aadt', label: 'Traffic AADT (veh/day)', kind: 'int',
        hint: 'Annual average daily traffic across the structure, in vehicles per day.' },
      { key: 'aadtYear', label: 'Year counted', kind: 'int',
        hint: 'Year the traffic count was taken, so an old count reads as old.' },
      { key: 'detourKm', label: 'Detour if closed (km)', kind: 'float',
        hint: 'Extra distance traffic would travel if the structure were closed, in kilometres.' },
      { key: 'detourMinutes', label: 'Detour time (min)', kind: 'int',
        hint: 'Extra journey time the detour adds, in minutes.' },
      { key: 'lifeline', label: 'Lifeline route', kind: 'bool',
        hint: 'Whether the road is a lifeline — the only access for emergency services or supplies.' },
    ],
  },
];

export const ALL_EDIT_FIELDS: EditField[] = EDIT_SECTIONS.flatMap((s) => s.fields);

/** A bridge's current values, one string per editable field, ready for a controlled input. */
export function draftFrom(b: Bridge): Record<string, string> {
  const out: Record<string, string> = {};
  for (const f of ALL_EDIT_FIELDS) {
    const v = b[f.key] as unknown;
    out[f.key] = v == null ? '' : String(v);
  }
  return out;
}

/**
 * Draft strings back into a JSON-ready PATCH body, or throws a message fit for the dialog.
 *
 * `asHeld` skips the form's required fields, for a write nobody typed — a fill
 * from a model — that must not refuse a record for gaps it did not make.
 */
export function buildEditPayload(draft: Record<string, string>, asHeld = false): Record<string, unknown> {
  const out: Record<string, unknown> = {};

  for (const f0 of ALL_EDIT_FIELDS) {
    const f = asHeld ? { ...f0, required: false } : f0;
    const raw = (draft[f.key] ?? '').trim();

    if (f.kind === 'text') {
      if (f.required && raw === '') throw new Error(`"${f.label}" is required.`);
      out[f.key] = raw === '' ? null : raw;
      continue;
    }

    /* Three states out, not two. An empty select is "nobody has recorded this",
       and sending `false` for it would have the register claiming an assessment
       that never happened. */
    if (f.kind === 'bool') {
      out[f.key] = raw === '' ? null : raw === 'true';
      continue;
    }

    if (raw === '') {
      if (f.required) throw new Error(`"${f.label}" is required.`);
      out[f.key] = null;
      continue;
    }

    const n = Number(raw);
    if (!Number.isFinite(n)) throw new Error(`"${f.label}" must be a number.`);
    if (f.kind === 'int' && !Number.isInteger(n)) throw new Error(`"${f.label}" must be a whole number.`);
    out[f.key] = n;
  }

  return out;
}
